'use client'

import { useEffect, useId, useRef, useState, useSyncExternalStore } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { ChevronDown } from 'lucide-react'
import { OptionWheel } from '@/components/ui/option-wheel'
import { DISTRICTS, getDistrictMeta, StatusPill, type DistrictSelectorValue } from './districts'

const WHEEL_ITEMS = DISTRICTS.map(({ name }) => name)
const DESKTOP_QUERY = '(min-width: 1024px)'
function subscribeToViewport(onChange: () => void) {
  const media = window.matchMedia(DESKTOP_QUERY)
  media.addEventListener('change', onChange)
  return () => media.removeEventListener('change', onChange)
}

export function DistrictPicker({ value, onChange }: {
  value: DistrictSelectorValue
  onChange: (district: DistrictSelectorValue) => void
}) {
  const [open, setOpen] = useState(false)
  const [preview, setPreview] = useState(value)
  const rootRef = useRef<HTMLDivElement>(null)
  const wheelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const latestPreview = useRef(value)
  const changeRef = useRef(onChange)
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wheelId = useId()
  const hintId = useId()
  const reducedMotion = useReducedMotion()
  const desktop = useSyncExternalStore(subscribeToViewport, () => window.matchMedia(DESKTOP_QUERY).matches, () => false)
  const place = getDistrictMeta(open ? preview : value)

  useEffect(() => { changeRef.current = onChange }, [onChange])
  useEffect(() => () => {
    if (settleTimer.current) clearTimeout(settleTimer.current)
  }, [])

  const close = (restoreFocus = false) => {
    if (settleTimer.current) clearTimeout(settleTimer.current)
    changeRef.current(latestPreview.current)
    setOpen(false)
    if (restoreFocus) triggerRef.current?.focus({ preventScroll: true })
  }

  useEffect(() => {
    if (!open) return
    wheelRef.current?.querySelector<HTMLElement>('[role="listbox"]')?.focus({ preventScroll: true })
    const dismissOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) {
        if (settleTimer.current) clearTimeout(settleTimer.current)
        changeRef.current(latestPreview.current)
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', dismissOutside)
    return () => document.removeEventListener('pointerdown', dismissOutside)
  }, [open])

  const browse = (index: number) => {
    const next = DISTRICTS[index].name
    latestPreview.current = next
    setPreview(next)
    if (settleTimer.current) clearTimeout(settleTimer.current)
    settleTimer.current = setTimeout(() => changeRef.current(next), 180)
  }

  return (
    <div ref={rootRef} onKeyDown={(event) => {
      if (open && event.key === 'Escape') {
        event.preventDefault()
        close(true)
      }
    }}>
      <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-2 lg:flex-nowrap">
        <h2 className="shrink-0 font-sans text-4xl tracking-[-0.03em] lg:text-5xl">
          Experiences in<span className="sr-only"> {place.name}</span>
        </h2>
        <div className="relative min-w-0 w-full lg:flex-1">
          <button
            ref={triggerRef}
            type="button"
            aria-label={`Change district, currently ${value}`}
            aria-expanded={open}
            aria-hidden={open || undefined}
            tabIndex={open ? -1 : 0}
            aria-controls={open ? wheelId : undefined}
            onClick={() => {
              if (open) { close(); return }
              latestPreview.current = value
              setPreview(value)
              setOpen(true)
            }}
            className={`inline-flex max-w-full items-center gap-3 border-b border-brand/50 py-1 text-left font-sans text-4xl tracking-[-0.03em] text-brand transition-colors hover:border-brand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/50 motion-reduce:transition-none lg:text-5xl ${open ? 'pointer-events-none absolute opacity-0' : ''}`}
          >
            <span className="min-w-0 break-words">{value}</span>
            <ChevronDown aria-hidden="true" className="size-6 shrink-0" />
          </button>
          {open && (
            <motion.div
              ref={wheelRef}
              id={wheelId}
              initial={reducedMotion ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.18 }}
              className="relative w-full"
            >
              <OptionWheel
                items={WHEEL_ITEMS}
                defaultSelected={DISTRICTS.findIndex(({ name }) => name === value)}
                onChange={browse}
                onSelect={(index) => {
                  latestPreview.current = DISTRICTS[index].name
                  close(true)
                }}
                onConfirm={() => close(true)}
                ariaLabel="Choose a district"
                ariaDescribedBy={hintId}
                activeColor="var(--brand)"
                textColor="var(--foreground)"
                fontSize={desktop ? 2 : 1.5}
                spacing={desktop ? 5.6 : 2.2}
                tilt={desktop ? 15 : 10}
                curve={desktop ? -0.85 : -0.15}
                rotateItems={false}
                blur={0}
                fade={0.2}
                minOpacity={0.45}
                smoothing={reducedMotion ? 1 : 140}
                inset={4}
                visibleItems={desktop ? 3.3 : 3.5}
                loop
                className="rounded-none focus-visible:ring-inset [touch-action:pan-y_pinch-zoom] [mask-image:linear-gradient(to_bottom,transparent,black_10%,black_90%,transparent)]"
                itemClassName="w-[calc(100%-2rem)] lg:w-[calc(100%-6rem)]"
                renderItem={(index, selected) => {
                  const district = DISTRICTS[index]
                  return (
                    <div className="grid w-full items-center gap-2 whitespace-normal lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-5">
                      <span className="block min-w-0">
                        <span className={`flex min-h-11 items-center justify-between gap-2 border-b py-2 ${selected ? 'border-brand text-brand' : 'border-transparent text-foreground'}`}>
                          <span className="break-words">{district.name}</span>
                          {selected && <ChevronDown className="size-5 shrink-0" aria-hidden="true" />}
                        </span>
                        {selected && (
                          <span className="mt-3 hidden lg:block">
                            <span className="block text-xs font-medium leading-5 tracking-normal text-muted-foreground">{district.character}</span>
                          </span>
                        )}
                      </span>
                      <span className="hidden text-sm font-normal leading-5 tracking-normal text-foreground lg:block">
                        <span className="mb-1 block text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-brand">
                          {district.status === 'live' ? `${district.count} experiences` : district.status === 'preview' ? 'Preview · opening next' : 'Coming soon'} · {district.themes}
                        </span>
                        <span className="line-clamp-2 lg:line-clamp-3">{district.tagline}</span>
                        {selected && (
                          <>
                            <span className="mt-3 block">
                              <span className="block text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-brand">
                                {district.status === 'live' ? 'Get closer to' : district.status === 'preview' ? 'In the preview' : 'Being explored'}
                              </span>
                              <span className="mt-1 block font-medium">{district.highlights.join(' · ')}</span>
                            </span>
                            <span className="mt-2 block text-xs leading-5 text-muted-foreground">{district.visitTip}</span>
                          </>
                        )}
                      </span>
                    </div>
                  )
                }}
              />
            </motion.div>
          )}
        </div>
      </div>
      {open && (
        <p id={hintId} className="mt-2 text-xs leading-5 text-muted-foreground lg:pl-[19rem]">
          Scroll or swipe to explore · Tap a place to choose
        </p>
      )}
      <div className={open ? 'mt-5 max-w-2xl lg:sr-only' : 'mt-6 max-w-2xl'} aria-hidden={open || undefined}>
        <div className="flex flex-wrap items-center gap-3">
          <StatusPill status={place.status} count={place.count} />
          <span className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-foreground/40">{place.themes}</span>
        </div>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{place.tagline}</p>
        {open && (
          <>
            <p className="mt-3 text-sm font-medium">{place.character}</p>
            <p className="mt-3 text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-brand">
              {place.status === 'live' ? 'Get closer to' : place.status === 'preview' ? 'In the preview' : 'Being explored'}
            </p>
            <p className="mt-1 text-sm leading-6">{place.highlights.join(' · ')}</p>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{place.visitTip}</p>
          </>
        )}
      </div>
    </div>
  )
}
